import React from 'react';
import '../../App.css';
import '../data-management/data-management.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// types
import {
  ComputeServiceType,
  DataItemType,
  RucioLocationsType,
  Site,
  SiteStorageArea,
} from '../../utils/types';
import { Event, EventType, EventTarget, MoveDataToStorage } from '../../utils/events';

// functions
import { ContainerHook, siteName } from '../../utils/functions';
import { getComputeServices } from '../interactive-sessions/interactive-sessions-actions';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { ContextMenu } from '../menus/context-menu';
import Scrollbox from '../../tools/scrollbox';
import SiteIcon from '../../tools/site-icon';
import ComputeSession from '../compute-tiles/compute-session';

// icons

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions (redux).
import { fnD_countAvailableFiles } from '../../app/data-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_DATA_STAGING as CONTEXT_MENU,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
  ctx_menuDisplayed,
} from '../menus/context-menu-actions';
import { handleEvent } from '../../app-actions';
import { getRucioSites } from '../data-management/file-browser-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function DataStagingComponent(props: {
  allItems: DataItemType[];
  selectedItems?: DataItemType[];
  catalog: string;
  contextSystem: CONTEXT_SYSTEMS;
  contextMenuRefresh?: any;
  hideItemCount?: boolean; // DEFAULT: false
  showComputeSessions?: boolean; // DEFAULT: false
}) {
  //

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state parameters.
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );
  const sD_storageAreas: SiteStorageArea[] = useAppSelector(
    (state) => state.data.storageAreas,
  );

  // lifetime.
  const sLifetimeStr: string = useAppSelector((state) => state.project.lifetimeStr);
  const sLifetime: number = useAppSelector((state) => state.project.lifetime);

  // context menu refresh.
  const sContextMenuRefresh: number = useAppSelector(
    (state) => state.project.contextMenuRefresh,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // retrieve the list of sites (in availability order) for the 'all items' and
  // 'selected items' lists.
  const sites: {
    site: Site;
    count: number;
  }[] = getRucioSites({
    allItems: props.allItems,
    selectedItems: props.selectedItems,
    catalog: props.catalog,
  });

  //	--------------------------------------------------------------------------
  //
  //	E V E N T   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Handler button clicks.
  //
  //	------------------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // prevent propogation of the onClick event.
    event.stopPropagation();

    // stage the data to the recommended site.
    if (button.name === 'stage-data') {
      //
      // build a list of selected files.
      let filesToMove: {
        namespace: string;
        filename: string;
        filetype: string;
        bytes: number;
      }[] = [];

      // add the selected items.
      if ((props.selectedItems ?? []).length > 0) {
        filesToMove = (props.selectedItems ?? []).map((map) => {
          return {
            namespace: map.namespace,
            filename: map.filename,
            filetype: 'FILE',
            bytes: 0,
          };
        });
      } else {
        filesToMove = props.allItems.map((map) => {
          return {
            namespace: map.namespace,
            filename: map.filename,
            filetype: 'FILE',
            bytes: 0,
          };
        });
      }

      if (sites.length > 0) {
        //
        const siteIndex: number = sD_sites.findIndex(
          (findSite) => findSite.site_uuid === sites[0].site.site_uuid,
        );
        const storageAreaIndex: number = 0;

        // moving data to a storage location.
        if (siteIndex > -1) {
          if (sD_storageAreas[siteIndex].storage_areas.length > 0) {
            //
            const moveDataToStorage: MoveDataToStorage = {
              toStorageAreaUUID:
                sD_storageAreas[siteIndex].storage_areas[storageAreaIndex].storage_id,
              toSiteUUID: sD_storageAreas[siteIndex].site.site_uuid ?? '',
              toStorageIdentifier:
                sD_storageAreas[siteIndex].storage_areas[storageAreaIndex].name,
              lifetime: 3600,
              filesToMove: filesToMove,
            };
            const newEvent: Event = {
              eventTarget: EventTarget.APP,
              eventType: EventType.MOVE_DATA_TO_STORAGE,
              parameters: moveDataToStorage,
            };
            dispatch(handleEvent({ event: newEvent }));

            //dispatch(setSubmittingJob(true));
            //
          }
        }
        //
      }
      //
    }

    // stage the data to a specific site.
    if (button.name === 'stage-to') {
      //
      // if the menu is currently displayed then hide it.
      if (ctx_menuDisplayed({ contextSystem: props.contextSystem }) === true) {
        dispatch(
          ctx_setContextVisible({
            contextSystem: props.contextSystem,
            visible: false,
          }),
        );
      } else {
        //
        // update the text in the main context menu.
        ctx_buildContextMenu({
          contextSystem: props.contextSystem,
          contextMenu: 1 /* SITES menu */,
          parentRowID: -1,
        });

        // check if this position is OK - use the button as a references point.
        ctx_calculatePosition({
          contextSystem: props.contextSystem,
          contextMenu: 1 /* SITES menu */,
          button: button.name,
        });

        dispatch(
          ctx_setContextVisible({
            contextSystem: props.contextSystem,
            contextMenu: 1 /* SITES menu */,
            visible: false,
          }),
        );
        dispatch(
          ctx_setContextVisible({
            contextSystem: props.contextSystem,
            contextMenu: 1 /* SITES menu */,
            visible: true,
          }),
        );

        dispatch(
          ctx_setContextRow({
            contextSystem: props.contextSystem,
            row: -1,
          }),
        );

        // refresh the context menu.
        if (props.contextMenuRefresh !== undefined) {
          dispatch(props.contextMenuRefresh(Date.now()));
        }

        // prevent propogation of the onClick event.
        event.stopPropagation();
        //
      }
      //
    }
    //
  }; // onClickBtnHandler

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    //
    //  Column to contain the file details.
    //
    <Scrollbox
      id="data-management-scrollbox"
      direction="vertical"
      width="300px"
      flex="1 1"
      flexDirection="column"
      padding="10px 10px 10px 10px"
      align="end"
    >
      {props.hideItemCount !== true && (
        <div
          className="text-colour"
          data-important="T"
          style={{ margin: '0px 0px 10px 0px' }}
        >
          {(props.selectedItems ?? []).length === 0
            ? props.allItems.length.toString() + ' item(s)'
            : (props.selectedItems ?? []).length.toString() + ' item(s) selected'}
        </div>
      )}
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'row',
          margin: '0px 0px 10px 0px',
        }}
      >
        <ToolButton
          key="stage-data"
          name="stage-data"
          onClick={onClickBtnHandler}
          tooltip="Stage data items"
          text="Stage data"
          disabled={sG_loggedIn !== true}
          type={ToolButtonType.PRIMARY}
        />
      </div>
      <div
        className="text-colour"
        style={{
          textAlign: 'right',
          marginBottom: '10px',
        }}
      >
        The data will be staged at:
      </div>
      {sites

        /*
        // filter to include only the first item.
        */

        .filter((site, index) => index === 0)

        /*
        // map each site to an icon, a site name, and a summary of the number of available data items.
        */

        .map((site) => (
          <div
            className="text-colour"
            style={{
              flex: '0 0 42px',
              width: '300px',
              display: 'flex',
              flexDirection: 'row',
              textAlign: 'center',
              alignItems: 'center',
              margin: '0px 0px 5px 2px',
            }}
          >
            <div
              style={{
                flex: '1 1',
                textAlign: 'right',
              }}
            >
              {'(' +
                fnD_countAvailableFiles({
                  site: site.site,
                  items:
                    (props.selectedItems ?? []).length === 0
                      ? props.allItems
                      : (props.selectedItems ?? []),
                }).toString() +
                ' / ' +
                ((props.selectedItems ?? []).length === 0
                  ? props.allItems.length.toString()
                  : (props.selectedItems ?? []).length.toString()) +
                ' available)'}
            </div>
            <div
              style={{
                flex: '0 0 105px',
                height: 'auto',
                textAlign: 'right',
                alignItems: 'center',
                fontSize: '10pt',
                userSelect: 'none',
              }}
            >
              {siteName({ site: site.site }).substring(0, 10)}
            </div>
            <div style={{ flex: '0 0 5px' }} />
            <SiteIcon site={site.site} size={32} />
          </div>
        ))}
      <div
        className="text-colour"
        style={{
          textAlign: 'right',
          marginBottom: '10px',
        }}
      >
        Alternatively, the data may be staged somewhere else:
      </div>
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'row',
          margin: '0px 0px 10px 0px',
        }}
      >
        <ToolButton
          key="Stage to"
          name="stage-to"
          onClick={onClickBtnHandler}
          tooltip="Stage data items to another site"
          text={'Stage to' + '  ▸'}
          disabled={sG_loggedIn !== true}
          type={ToolButtonType.SECONDARY}
        />
      </div>
      {sites.length > 1 && (
        <div
          className="text-colour"
          style={{
            textAlign: 'right',
            marginBottom: '10px',
          }}
        >
          The data are also located at the following sites:
        </div>
      )}
      {sites

        /*
        // filter to remove the first item.
        */

        .filter((site, index) => index > 0)

        /*
        // map each site to an icon, a site name, and a summary of the number of available data items.
        */

        .map((site) => (
          <div
            className="text-colour"
            style={{
              flex: '0 0 42px',
              width: '300px',
              display: 'flex',
              flexDirection: 'row',
              textAlign: 'center',
              alignItems: 'center',
              margin: '0px 0px 5px 2px',
            }}
          >
            <div
              style={{
                flex: '1 1',
                textAlign: 'right',
              }}
            >
              {'(' +
                fnD_countAvailableFiles({
                  site: site.site,
                  items:
                    (props.selectedItems ?? []).length === 0
                      ? props.allItems
                      : (props.selectedItems ?? []),
                }) +
                ' / ' +
                ((props.selectedItems ?? []).length === 0
                  ? props.allItems.length.toString()
                  : (props.selectedItems ?? []).length.toString()) +
                ' available)'}
            </div>
            <div
              style={{
                flex: '0 0 105px',
                height: 'auto',
                textAlign: 'right',
                alignItems: 'center',
                fontSize: '10pt',
                userSelect: 'none',
              }}
            >
              {siteName({ site: site.site }).substring(0, 10)}
            </div>
            <div style={{ flex: '0 0 5px' }} />
            <SiteIcon site={site.site} size={32} />
          </div>
        ))}
      {props.showComputeSessions === true &&
        sites

          /*
          // filter to include only the first item.
          */

          .filter((site: { site: Site; count: number }, index: number) => index === 0)

          /*
          // map each site to a list of compute session tiles.
          */

          .map((site: { site: Site; count: number }) => (
            <>
              {getComputeServices({ site: site.site }).map((item: ComputeServiceType) => (
                <ComputeSession session={item} />
              ))}
              ;
            </>
          ))}
    </Scrollbox>
  );
} // DataStagingComponent
